import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  CssBaseline,
  Grid,
  Toolbar,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import WebIcon from "@mui/icons-material/Web";
import GroupsIcon from "@mui/icons-material/Groups";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";

const adminFeatures = [
  {
    id: "docs",
    title: "Documentation",
    description: "Admin documentation on how things work",
    icon: <MenuBookIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/docs",
    status: "available",
  },
  {
    id: "organizations",
    title: "Organizations",
    description: "View and manage all organizations in the system",
    icon: <BusinessIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/organizations",
    status: "available",
    docTopic: "organizations",
  },
  {
    id: "users",
    title: "Users",
    description: "Manage user accounts and permissions",
    icon: <PeopleIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/users",
    status: "available",
    docTopic: "auth0",
  },
  {
    id: "contacts",
    title: "Contacts",
    description: "View and manage all contacts across organizations",
    icon: <ContactMailIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/contacts",
    status: "available",
  },
  {
    id: "subscriptions",
    title: "Subscriptions",
    description: "Manage all subscription records",
    icon: <SubscriptionsIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/subscriptions",
    status: "available",
    docTopic: "subscriptions",
  },
  {
    id: "orders",
    title: "Orders",
    description: "View and manage all orders",
    icon: <ShoppingCartIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/orders",
    status: "available",
  },
  {
    id: "products",
    title: "Products",
    description: "View and manage all products available on the site",
    icon: <InventoryIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/products",
    status: "available",
  },
  {
    id: "auth0-accounts",
    title: "Auth0 Accounts",
    description: "View and manage all Auth0 accounts in the system",
    icon: <AccountCircleIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/accounts",
    status: "available",
    docTopic: "auth0",
  },
  {
    id: "squarespace",
    title: "Squarespace",
    description: "Manage Squarespace integrations and features",
    icon: <WebIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace",
    status: "available",
    docTopic: "squarespace-website",
  },
  {
    id: "circle",
    title: "Circle",
    description: "Manage Circle community events and members",
    icon: <GroupsIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/circle",
    status: "available",
    docTopic: "circle-events",
  },
  {
    id: "subscription-plans",
    title: "Subscription Plans",
    description: "Manage subscription plan templates",
    icon: <CardMembershipIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/subscription-plans",
    status: "available",
    docTopic: "subscriptions",
  },
  {
    id: "env",
    title: "API Environment",
    description: "View API env vars (confirm required config)",
    icon: <SettingsIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/env",
    status: "available",
    docTopic: "api-environment",
  },
];

export default function AdminFeatures() {
  const navigate = useNavigate();

  const handleFeatureClick = (feature) => {
    if (feature.status === "available") {
      navigate(feature.route);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component="a"
                  href="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Admin
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Admin Panel"
              description="Manage system resources and configurations"
              icon={<AdminPanelSettingsIcon sx={{ color: "primary.main" }} />}
            />

            <Grid container spacing={3}>
              {adminFeatures.map((feature) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.id}>
                  <Card
                    sx={{
                      borderRadius: feature.id === "squarespace" || feature.id === "circle" ? 3 : 2,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: feature.status === "available" ? "pointer" : "default",
                      opacity: feature.status === "coming-soon" ? 0.7 : 1,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      ...((feature.id === "squarespace" || feature.id === "circle") && {
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.1)} 100%)`
                            : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.05)} 100%)`,
                        border: (theme) =>
                          `2px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.3 : 0.18)}`,
                      }),
                      ...(feature.id !== "squarespace" && feature.id !== "circle" && {
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)"
                            : `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.04)}`,
                        border: (theme) =>
                          `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.06) : alpha(theme.palette.primary.main, 0.06)}`,
                      }),
                      ...((feature.id === "squarespace" || feature.id === "circle") && {
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? `0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                            : `0 4px 24px ${alpha(theme.palette.primary.main, 0.12)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                      }),
                      "&:hover": feature.status === "available"
                        ? {
                            transform: feature.id === "squarespace" || feature.id === "circle"
                              ? "translateY(-8px) scale(1.03)"
                              : "translateY(-8px) scale(1.02)",
                            boxShadow: (theme) => {
                              const c = theme.palette.primary.main;
                              const s = theme.palette.secondary?.main || c;
                              return feature.id === "squarespace" || feature.id === "circle"
                                ? (theme.palette.mode === "dark"
                                  ? `0 28px 56px rgba(0,0,0,0.45), 0 0 0 1.5px ${alpha(s, 0.3)}`
                                  : `0 28px 56px ${alpha(c, 0.2)}, 0 0 0 1.5px ${alpha(c, 0.18)}`)
                                : theme.palette.mode === "dark"
                                  ? `0 24px 48px rgba(0,0,0,0.4), 0 0 0 1px ${alpha(s, 0.15)}`
                                  : `0 24px 48px ${alpha(c, 0.15)}, 0 0 0 1px ${alpha(c, 0.08)}`;
                            },
                          }
                        : {},
                    }}
                    onClick={() => handleFeatureClick(feature)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 56,
                            height: 56,
                            borderRadius: 1.5,
                            background: (theme) =>
                              alpha(
                                theme.palette.primary.main,
                                feature.id === "squarespace" || feature.id === "circle"
                                  ? theme.palette.mode === "dark"
                                    ? 0.2
                                    : 0.12
                                  : theme.palette.mode === "dark"
                                    ? 0.12
                                    : 0.06
                              ),
                            "& .MuiSvgIcon-root": { fontSize: 36 },
                          }}
                        >
                          {feature.icon}
                        </Box>
                        {feature.status === "coming-soon" && (
                          <Chip
                            label="Coming Soon"
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        )}
                        {feature.status === "available" && (
                          <Chip
                            label="Available"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography
                        variant="h6"
                        component="h2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                    {feature.status === "available" && (
                      <CardActions sx={{ px: 2, pb: 2, flexDirection: "column", alignItems: "stretch", gap: 0 }}>
                        <Stack spacing={1} sx={{ width: "100%" }}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(feature.route);
                            }}
                            sx={{
                              py: 1.25,
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              transition: "all 0.25s ease",
                              "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: (theme) =>
                                  `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                              },
                            }}
                          >
                            Open
                          </Button>
                          {feature.docTopic && (
                            <Button
                              variant="outlined"
                              color="secondary"
                              fullWidth
                              size="small"
                              startIcon={<MenuBookIcon fontSize="small" />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/docs?topic=${feature.docTopic}`);
                              }}
                            >
                              Documentation
                            </Button>
                          )}
                        </Stack>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
}
